import React from 'react';
import { AlertTriangle, TrendingDown, DollarSign, Shield, Info } from 'lucide-react';
import '../styles/LegalPages.css';

const DisclaimerPage = () => {
  return (
    <div className="legal-page">
      <div className="legal-container">
        <h1 className="legal-title">Risk Disclaimer</h1>
        <p className="legal-updated">Last Updated: December 2025</p>

        <div className="disclaimer-warning">
          <AlertTriangle size={48} />
          <h2>IMPORTANT RISK WARNING</h2>
          <p>
            Trading financial markets involves substantial risk of loss and is not suitable for all investors. 
            You should carefully consider your financial situation, investment objectives, and risk tolerance 
            before engaging in any trading activity.
          </p>
        </div>

        <div className="legal-intro">
          <p>
            This Risk Disclaimer provides important information about the risks associated with using 
            Market Sentinel AI and engaging in financial market trading. Please read this carefully 
            and ensure you understand the risks before using our service.
          </p>
        </div>

        <section className="legal-section">
          <div className="legal-section-header">
            <Info size={24} />
            <h2>Nature of Service</h2>
          </div>
          <div className="legal-content">
            <h3>Market Sentinel AI is an Analytical Tool Only</h3>
            <p>
              Market Sentinel AI provides:
            </p>
            <ul>
              <li>Automated market scanning and analysis</li>
              <li>AI-generated trade setup identification</li>
              <li>Market narrative and opportunity alerts</li>
              <li>Technical analysis across multiple timeframes</li>
            </ul>

            <h3>What We Are NOT</h3>
            <p>
              <strong>Market Sentinel AI does NOT:</strong>
            </p>
            <ul>
              <li>Provide financial advice, investment advice, or trading recommendations</li>
              <li>Act as a broker, dealer, investment advisor, or financial planner</li>
              <li>Guarantee profits, returns, or trading success</li>
              <li>Execute trades or manage your funds</li>
              <li>Provide personalized advice based on your individual circumstances</li>
            </ul>

            <div className="disclaimer-box">
              <p>
                <strong>EXPLICIT STATEMENT:</strong> All information, analysis, and alerts provided by 
                Market Sentinel AI are for educational and informational purposes only. Nothing in our 
                Service constitutes financial advice or a recommendation to buy, sell, or hold any asset.
              </p>
            </div>
          </div>
        </section>

        <section className="legal-section">
          <div className="legal-section-header">
            <TrendingDown size={24} />
            <h2>Trading Risks</h2>
          </div>
          <div className="legal-content">
            <h3>General Trading Risks</h3>
            <p>
              Trading financial markets carries inherent risks, including but not limited to:
            </p>

            <h4>Risk of Loss</h4>
            <ul>
              <li><strong>You can lose money.</strong> There is substantial risk of loss in trading</li>
              <li>Losses can exceed your initial investment, especially with leveraged products</li>
              <li>Past performance does not indicate future results</li>
              <li>No trading system or strategy can guarantee profits</li>
            </ul>

            <h4>Market Volatility</h4>
            <ul>
              <li>Financial markets can be extremely volatile</li>
              <li>Prices can change rapidly and unpredictably</li>
              <li>Market gaps and slippage can result in unexpected losses</li>
              <li>Volatility can be especially severe during news events or low liquidity periods</li>
            </ul>

            <h4>Leverage Risk</h4>
            <ul>
              <li>Leveraged trading amplifies both gains and losses</li>
              <li>Small market movements can result in large account swings</li>
              <li>Margin calls can force liquidation of positions at unfavorable prices</li>
              <li>You can lose more than your initial deposit when using leverage</li>
            </ul>
          </div>
        </section>

        <section className="legal-section">
          <div className="legal-section-header">
            <DollarSign size={24} />
            <h2>Asset-Specific Risks</h2>
          </div>
          <div className="legal-content">
            <h3>Cryptocurrency Markets</h3>
            <p>
              Cryptocurrency trading involves unique and elevated risks:
            </p>
            <ul>
              <li><strong>Extreme Volatility:</strong> Crypto prices can swing dramatically in short periods</li>
              <li><strong>24/7 Markets:</strong> Cryptocurrency markets never close, increasing exposure</li>
              <li><strong>Regulatory Uncertainty:</strong> Evolving regulations can impact asset values</li>
              <li><strong>Security Risks:</strong> Exchange hacks, wallet vulnerabilities, and scams</li>
              <li><strong>Liquidity:</strong> Some cryptocurrencies have limited liquidity</li>
              <li><strong>Technology Risk:</strong> Blockchain forks, network issues, and technical failures</li>
              <li><strong>Irreversible Transactions:</strong> Cryptocurrency transactions cannot be reversed</li>
            </ul>

            <h3>Forex Markets</h3>
            <p>
              Foreign exchange (forex) trading carries specific risks:
            </p>
            <ul>
              <li><strong>Leverage:</strong> Forex brokers typically offer high leverage, magnifying risk</li>
              <li><strong>Currency Risk:</strong> Exchange rate fluctuations can be sudden and severe</li>
              <li><strong>Interest Rate Risk:</strong> Central bank policies affect currency values</li>
              <li><strong>Political Risk:</strong> Geopolitical events can cause rapid price movements</li>
              <li><strong>Counterparty Risk:</strong> Risk that your broker may fail or act improperly</li>
            </ul>

            <h3>Macro Assets (Indices, Commodities, Bonds)</h3>
            <p>
              Trading macro assets involves:
            </p>
            <ul>
              <li><strong>Market Risk:</strong> Broad market movements can affect positions</li>
              <li><strong>Economic Risk:</strong> Economic indicators and data releases cause volatility</li>
              <li><strong>Commodity-Specific Risks:</strong> Supply/demand factors, weather, geopolitics</li>
              <li><strong>Index Composition:</strong> Changes in index components can affect performance</li>
            </ul>
          </div>
        </section>

        <section className="legal-section">
          <div className="legal-section-header">
            <Shield size={24} />
            <h2>Technology and Service Risks</h2>
          </div>
          <div className="legal-content">
            <h3>Analytical Limitations</h3>
            <ul>
              <li><strong>No Guarantees:</strong> Our analysis does not guarantee accuracy or profitability</li>
              <li><strong>AI Limitations:</strong> AI models can make errors or misinterpret market conditions</li>
              <li><strong>Historical Bias:</strong> Analysis is based on historical data that may not predict future outcomes</li>
              <li><strong>Market Changes:</strong> Market dynamics can change, reducing effectiveness of strategies</li>
              <li><strong>False Signals:</strong> Technical analysis can generate false or misleading signals</li>
            </ul>

            <h3>Technical Risks</h3>
            <ul>
              <li><strong>System Failures:</strong> App outages, server issues, or connectivity problems</li>
              <li><strong>Data Delays:</strong> Market data may be delayed or inaccurate</li>
              <li><strong>Notification Failures:</strong> Alerts may not be delivered timely or at all</li>
              <li><strong>Device Issues:</strong> Your device or internet connection may fail</li>
            </ul>

            <h3>No Liability for Losses</h3>
            <div className="disclaimer-box">
              <p>
                <strong>IMPORTANT:</strong> Market Sentinel AI is not responsible for any trading losses, 
                missed opportunities, or damages resulting from your use of the Service, including but not 
                limited to losses caused by inaccurate analysis, technical failures, delayed notifications, 
                or any other reason.
              </p>
            </div>
          </div>
        </section>

        <section className="legal-section">
          <h2>Your Responsibility</h2>
          <div className="legal-content">
            <h3>Independent Decision Making</h3>
            <p>
              <strong>YOU are solely responsible for your trading decisions.</strong>
            </p>
            <ul>
              <li>Conduct your own research and due diligence</li>
              <li>Understand the markets and assets you trade</li>
              <li>Never trade based solely on automated analysis or alerts</li>
              <li>Consider seeking advice from qualified financial professionals</li>
              <li>Only trade with capital you can afford to lose</li>
            </ul>

            <h3>Risk Management</h3>
            <p>
              It is your responsibility to:
            </p>
            <ul>
              <li>Implement appropriate risk management strategies</li>
              <li>Set stop-losses and position limits</li>
              <li>Diversify your portfolio appropriately</li>
              <li>Monitor your positions and account regularly</li>
              <li>Understand the terms and risks of your trading platform</li>
            </ul>

            <h3>Suitability Assessment</h3>
            <p>
              Before trading, honestly assess whether it is suitable for you:
            </p>
            <ul>
              <li>Do you understand the markets and instruments?</li>
              <li>Can you afford to lose your trading capital?</li>
              <li>Do you have adequate financial resources and emergency reserves?</li>
              <li>Can you handle the emotional stress of trading?</li>
              <li>Do you have realistic expectations about returns?</li>
            </ul>
          </div>
        </section>

        <section className="legal-section">
          <h2>Regulatory Considerations</h2>
          <div className="legal-content">
            <h3>Not Regulated as Financial Advisor</h3>
            <p>
              Market Sentinel AI operates as a software tool and analytical platform. We are not registered 
              or licensed as:
            </p>
            <ul>
              <li>Investment advisor</li>
              <li>Broker-dealer</li>
              <li>Commodity trading advisor</li>
              <li>Financial planner or consultant</li>
            </ul>

            <h3>Your Jurisdiction</h3>
            <p>
              You are responsible for:
            </p>
            <ul>
              <li>Understanding and complying with laws in your jurisdiction</li>
              <li>Ensuring trading is legal where you reside</li>
              <li>Meeting any licensing or registration requirements</li>
              <li>Paying applicable taxes on trading gains</li>
            </ul>

            <h3>Broker Relationship</h3>
            <p>
              Market Sentinel AI does not have brokerage relationships or partnerships. Your trading is 
              conducted through your independently chosen broker or exchange. We are not responsible for:
            </p>
            <ul>
              <li>Broker actions, policies, or failures</li>
              <li>Trade execution quality or pricing</li>
              <li>Broker insolvency or fraud</li>
              <li>Withdrawal issues or account restrictions</li>
            </ul>
          </div>
        </section>

        <section className="legal-section">
          <h2>Performance Claims and Hypothetical Results</h2>
          <div className="legal-content">
            <h3>No Performance Guarantees</h3>
            <p>
              Any reference to historical performance, win rates, or profitability:
            </p>
            <ul>
              <li>Represents past results that do not guarantee future performance</li>
              <li>May be based on hypothetical or backtested results</li>
              <li>Does not account for slippage, commissions, or real-world execution</li>
              <li>May not be representative of results achieved by typical users</li>
            </ul>

            <h3>Hypothetical Results Limitations</h3>
            <p>
              <strong>HYPOTHETICAL PERFORMANCE RESULTS HAVE INHERENT LIMITATIONS:</strong>
            </p>
            <ul>
              <li>They are designed with the benefit of hindsight</li>
              <li>They do not involve financial risk</li>
              <li>They cannot account for the impact of actual trading</li>
              <li>Actual results may differ materially from hypothetical results</li>
            </ul>
          </div>
        </section>

        <section className="legal-section">
          <h2>Educational Purpose</h2>
          <div className="legal-content">
            <p>
              Market Sentinel AI is designed for educational and informational purposes. Our goal is to:
            </p>
            <ul>
              <li>Help users learn about market analysis</li>
              <li>Demonstrate technical analysis concepts</li>
              <li>Provide tools for market research</li>
              <li>Facilitate informed decision-making</li>
            </ul>
            <p>
              However, education does not eliminate risk. Even experienced traders can and do lose money. 
              Consider paper trading or practicing with small amounts before committing significant capital.
            </p>
          </div>
        </section>

        <section className="legal-section">
          <h2>Acknowledgment and Acceptance</h2>
          <div className="legal-content">
            <div className="disclaimer-box disclaimer-final">
              <p>
                <strong>BY USING MARKET SENTINEL AI, YOU ACKNOWLEDGE AND AGREE THAT:</strong>
              </p>
              <ul>
                <li>You have read and understood this Risk Disclaimer</li>
                <li>You understand that trading involves substantial risk of loss</li>
                <li>You are solely responsible for your trading decisions and outcomes</li>
                <li>Market Sentinel AI provides analytical tools only, not financial advice</li>
                <li>No guarantees are made regarding accuracy, profitability, or results</li>
                <li>You will not hold Market Sentinel AI liable for any losses or damages</li>
                <li>You will only trade with capital you can afford to lose</li>
                <li>You may seek independent financial advice before trading</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="legal-section legal-contact">
          <h2>Questions About Risk</h2>
          <div className="legal-content">
            <p>
              If you have questions about the risks of trading or using Market Sentinel AI:
            </p>
            <ul>
              <li>Review our FAQs and educational resources within the app</li>
              <li>Contact our support team: support@market-sentinel.net</li>
              <li>Consult with qualified financial professionals</li>
              <li>Visit https://market-sentinel.net for more information</li>
            </ul>
            <p>
              <strong>Do not use Market Sentinel AI if you do not understand or accept these risks.</strong>
            </p>
          </div>
        </section>
      </div>
    </div>
  );
};

export default DisclaimerPage;
